import React from "react";
import { Link, useParams } from "react-router-dom";
import SEO from "../components/SEO";
import { tours } from "../data/tours";

/**
 * One tour, in the order somebody decides in: what it is, what you'll see,
 * then day by day, then what the price does and does not cover.
 *
 * Price is rendered only when the tour actually carries one. The itinerary
 * comes from the operator's own document, which quotes no price and no review
 * score — so this asks people to enquire rather than showing a number nobody
 * set.
 */

type Day = {
  day: number;
  title: string;
  description: string;
  image?: string;
  imageAlt?: string;
  stay?: string;
  notes?: string[];
};

type Tour = {
  id: string;
  slug: string;
  title: string;
  category: string;
  duration: string;
  difficulty: string;
  activityLevel?: string;
  bestFor?: string;
  pickup?: string;
  note?: string;
  location: string;
  route?: string;
  price?: number | null;
  priceNote?: string;
  image: string;
  shortDesc: string;
  longDesc: string;
  highlights: string[];
  itinerary: Day[];
  inclusions?: string[];
  exclusions?: string[];
};

const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div>
    <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">{label}</dt>
    <dd className="text-gray-900 font-medium mt-0.5">{children}</dd>
  </div>
);

export default function TourDetailPage() {
  const { id } = useParams();
  const tour = (tours as Tour[]).find((t) => t.id === id || t.slug === id);

  if (!tour) {
    return (
      <section className="py-24 px-4 text-center">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Tour not found</h1>
        <p className="text-gray-500 mb-6">That tour may have been renamed or retired.</p>
        <Link to="/tours" className="btn-primary">See all tours</Link>
      </section>
    );
  }

  return (
    <>
      <SEO title={`${tour.title} — D Trio Trek`} description={tour.shortDesc} image={tour.image} />

      {/* Hero */}
      <section className="relative h-[60vh] min-h-[380px] flex items-end">
        <img
          src={tour.image}
          alt={tour.title}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
        <div className="relative max-w-5xl mx-auto w-full px-4 pb-10 text-white">
          <p className="text-sm font-medium tracking-wide uppercase opacity-90 mb-2">
            {tour.location}
          </p>
          <h1 className="text-3xl md:text-5xl font-bold leading-tight max-w-3xl">
            {tour.title}
          </h1>
          <p className="mt-3 text-white/90 max-w-2xl">{tour.shortDesc}</p>
        </div>
      </section>

      {/* The facts somebody checks before reading anything else */}
      <section className="border-b border-gray-200 bg-white">
        <dl className="max-w-5xl mx-auto px-4 py-6 grid grid-cols-2 md:grid-cols-4 gap-6">
          <Fact label="Duration">{tour.duration}</Fact>
          <Fact label={tour.activityLevel ? "Activity level" : "Best for"}>
            {tour.activityLevel || tour.bestFor || tour.difficulty}
          </Fact>
          <Fact label="Route">{tour.route || tour.location}</Fact>
          <Fact label="Price">
            {typeof tour.price === "number" ? (
              <>
                ${tour.price.toLocaleString()}
                <span className="text-gray-500 font-normal text-sm"> per person</span>
              </>
            ) : (
              <>
                {tour.priceNote || "On request"}
                <span className="block text-gray-500 font-normal text-sm">
                  <Link to="/contact" className="text-primary-600 hover:underline">
                    Ask for a quote
                  </Link>
                </span>
              </>
            )}
          </Fact>
        </dl>
      </section>

      <div className="max-w-5xl mx-auto px-4 py-12 grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-12">
          {/* Overview */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">About this expedition</h2>
            <p className="text-gray-600 leading-relaxed">{tour.longDesc}</p>
            {tour.note && (
              <p className="mt-3 text-sm text-gray-500 italic">{tour.note}</p>
            )}
          </section>

          {/* Highlights */}
          {tour.highlights?.length > 0 && (
            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Tour highlights</h2>
              <ul className="space-y-2.5">
                {tour.highlights.map((h, i) => (
                  <li key={i} className="flex gap-3 text-gray-700">
                    <svg
                      className="w-5 h-5 flex-shrink-0 text-primary-600 mt-0.5"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{h}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* Day by day — the reason anybody opens this page */}
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-6">Day-by-day itinerary</h2>
            <ol className="space-y-10">
              {tour.itinerary.map((d, idx) => (
                <li key={d.day} className="relative pl-12">
                  {/* Numbered and joined by a line: a 7-day trip should read as
                      one sequence, not seven unrelated cards. */}
                  <span className="absolute left-0 top-0 w-9 h-9 rounded-full bg-primary-600 text-white font-bold text-sm flex items-center justify-center">
                    {d.day}
                  </span>
                  {idx < tour.itinerary.length - 1 && (
                    <span
                      className="absolute left-[17px] top-11 -bottom-10 w-px bg-gray-200"
                      aria-hidden="true"
                    />
                  )}

                  <h3 className="font-bold text-lg text-gray-900 mb-2">{d.title}</h3>
                  {d.image && (
                    <img
                      src={d.image}
                      alt={d.imageAlt || d.title}
                      loading="lazy"
                      className="w-full h-56 md:h-72 object-cover rounded-xl mb-3"
                    />
                  )}
                  <p className="text-gray-600 leading-relaxed">{d.description}</p>

                  {d.stay && d.stay !== "—" && (
                    <p className="mt-2 text-sm text-gray-500">
                      <span className="font-semibold text-gray-700">Overnight:</span> {d.stay}
                    </p>
                  )}

                  {!!d.notes?.length && (
                    <div className="mt-3 rounded-lg bg-gray-50 border border-gray-200 p-3 space-y-2">
                      {d.notes.map((n, i) => (
                        <p key={i} className="text-sm text-gray-600 leading-relaxed">
                          {n}
                        </p>
                      ))}
                    </div>
                  )}
                </li>
              ))}
            </ol>
          </section>

          {/* What the price does and does not cover, side by side — the pair is
              the point, and separating them is how a guest gets surprised. */}
          {(!!tour.inclusions?.length || !!tour.exclusions?.length) && (
            <section className="grid sm:grid-cols-2 gap-6">
              {!!tour.inclusions?.length && (
                <div className="rounded-xl border border-gray-200 p-5">
                  <h3 className="font-bold text-gray-900 mb-3">Included</h3>
                  <ul className="space-y-2">
                    {tour.inclusions.map((x, i) => (
                      <li key={i} className="flex gap-2 text-sm text-gray-700">
                        <span className="text-green-600 font-bold flex-shrink-0">✓</span>
                        <span>{x}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {!!tour.exclusions?.length && (
                <div className="rounded-xl border border-gray-200 p-5">
                  <h3 className="font-bold text-gray-900 mb-3">Not included</h3>
                  <ul className="space-y-2">
                    {tour.exclusions.map((x, i) => (
                      <li key={i} className="flex gap-2 text-sm text-gray-700">
                        <span className="text-gray-400 font-bold flex-shrink-0">✕</span>
                        <span>{x}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          )}
        </div>

        {/* Enquiry card — sticky, because the decision gets made while reading
            day 5, not after scrolling back to the top. */}
        <aside className="lg:col-span-1">
          <div className="lg:sticky lg:top-24 rounded-2xl border border-gray-200 p-6 card-shadow bg-white">
            <p className="text-sm text-gray-500">Price</p>
            <p className="text-2xl font-bold text-gray-900 mb-1">
              {typeof tour.price === "number"
                ? `$${tour.price.toLocaleString()}`
                : tour.priceNote || "On request"}
            </p>
            <p className="text-sm text-gray-500 mb-5">
              Tours run privately, so the cost depends on group size and season.
            </p>
            <Link to="/contact" className="btn-primary w-full text-center block mb-3">
              Enquire about this tour
            </Link>
            <a
              href="https://wa.me/923102329666"
              target="_blank"
              rel="noreferrer"
              className="btn-outline w-full text-center block"
            >
              WhatsApp +92 310 2329666
            </a>
            <dl className="mt-6 pt-5 border-t border-gray-200 space-y-3 text-sm">
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Duration</dt>
                <dd className="text-gray-900 font-medium text-right">{tour.duration}</dd>
              </div>
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Based in</dt>
                <dd className="text-gray-900 font-medium text-right">{tour.location}</dd>
              </div>
              {tour.pickup && (
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-500">Pick-up</dt>
                  <dd className="text-gray-900 font-medium text-right">{tour.pickup}</dd>
                </div>
              )}
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Operator</dt>
                <dd className="text-gray-900 font-medium text-right">D Trio Trek</dd>
              </div>
            </dl>
          </div>
        </aside>
      </div>
    </>
  );
}
